import { RARITY_IDS } from "@highmarch/domain";
import { audio } from "../audio/audio";
import type { AudioCue } from "../audio/audioPort";
import { uiUrl } from "./portraits";
import { CARD_BEAT_MS, CARD_FLIGHT, cardHoldMs, cardOrder } from "./revealPlan";

/**
 * The reward cards on the chest stage (chests v8 wow pass): they fly out of the chest's mouth face-down, then turn over
 * one at a time, worst first and the best last. A card of epic or better is held before it turns — it shakes and glows
 * in its colour while light fans out behind it — then flips with its rarity's stinger and a "LEGENDARY!" pop; the best of
 * them is held longest and stays lifted in a spotlight. Every back is the same plain card, so only the glow gives a card
 * away before it turns. `finish` jumps to the end (a tap); `onDone` when the last card is face-up.
 */
export interface CardDeal { finish(): void }

/**
 * What a face-up reward card opens when tapped once the deal is done (UI pass 2026-10-21: tap an item to see it in
 * focus, as the shops do). chestReveal.ts registers a gear card's focus as it builds the card; the loot card has none.
 */
export const cardFocus = new WeakMap<HTMLElement, () => void>();

/** A card's rank for the order: its rarity's index, the loot card (gold, speed-ups) −1 so it turns first. */
function rankOf(card: HTMLElement): number {
  if (card.classList.contains("loot")) return -1;
  const i = RARITY_IDS.findIndex((r) => card.classList.contains(r));
  return Math.max(0, i);
}

const STINGER: readonly AudioCue[] = ["cardFlip", "cardFlip", "cardRare", "rarityEpic", "rarityLegendary", "rarityMythic"];

export function dealCards(box: HTMLElement, cards: readonly HTMLElement[], from: { x: number; y: number }, motion: boolean, onDone: () => void): CardDeal {
  const ranks = cards.map(rankOf);
  const order = cardOrder(ranks);
  const best = Math.max(...ranks);
  const bestAt = order[order.length - 1];
  const timers: number[] = [];
  const anims: Animation[] = [];
  let finished = false;
  const later = (ms: number, fn: () => void) => timers.push(window.setTimeout(() => { if (box.isConnected && !finished) fn(); }, ms));

  // the slots in reveal order, every back the same plain card
  const slots = order.map((ci) => {
    const card = cards[ci], rank = ranks[ci];
    const slot = document.createElement("div");
    slot.className = `rs-slot${rank >= 0 ? ` r-${RARITY_IDS[rank]}` : " r-loot"}`;
    slot.innerHTML = `<i class="rs-glow"></i><i class="rs-fan"></i><div class="rs-fx"><div class="rs-fy"><div class="rs-flip"><div class="rs-face back"><img decoding="async" src="${uiUrl("chests/itemcard_back.png")}" alt=""><b class="disp">?</b></div><div class="rs-face front"></div></div></div></div>`;
    slot.querySelector(".front")!.append(card);
    if (rank >= 3) slot.querySelector(".front")!.insertAdjacentHTML("beforeend", `<i class="rs-shine"></i>`);
    box.append(slot);
    return { slot, rank, best: ci === bestAt && best >= 3 };
  });

  const flipNow = (s: (typeof slots)[number]) => {
    s.slot.classList.remove("charging");
    s.slot.classList.add("flipped");
    if (s.rank >= 3) {
      s.slot.classList.add("spot");
      if (s.best) s.slot.classList.add("best");
      s.slot.insertAdjacentHTML("beforeend", `<b class="rs-pop disp">${RARITY_IDS[s.rank].toUpperCase()}!</b>`);
      audio.play(STINGER[s.rank]);
    } else audio.play(s.rank === 2 ? "cardRare" : "cardFlip");
    if (!motion) return;
    const flip = s.slot.querySelector<HTMLElement>(".rs-flip")!;
    anims.push(flip.animate([{ transform: "rotateY(180deg) scale(1)" }, { transform: "rotateY(90deg) scale(1.12)", offset: 0.45 }, { transform: "rotateY(0deg) scale(1)" }],
      { duration: CARD_FLIGHT.flipMs + (s.rank >= 3 ? 120 : 0), easing: "cubic-bezier(.3,1.3,.5,1)", fill: "both" }));
    const shine = s.slot.querySelector<HTMLElement>(".rs-shine");
    if (shine) anims.push(shine.animate([{ transform: "translateX(-160%) skewX(-18deg)" }, { transform: "translateX(360%) skewX(-18deg)" }], { duration: 650, delay: 260, easing: "ease-in-out" }));
  };

  const finish = () => {
    if (finished) return;
    finished = true;
    for (const t of timers) window.clearTimeout(t);
    for (const a of anims) { try { a.finish(); } catch { /* a cancelled animation */ } }
    for (const s of slots) {
      s.slot.classList.add("live", "flipped");
      s.slot.classList.remove("charging");
      if (s.rank >= 3) s.slot.classList.add("spot");
      if (s.best) s.slot.classList.add("best");
    }
    onDone();
  };

  if (!motion) { finish(); return { finish }; }

  // 1. out of the mouth, face-down, one after another
  slots.forEach((s, i) => {
    s.slot.classList.add("live");
    const r = s.slot.getBoundingClientRect();
    const dx = from.x - (r.left + r.width / 2), dy = from.y - (r.top + r.height / 2);
    const fx = s.slot.querySelector<HTMLElement>(".rs-fx")!, fy = s.slot.querySelector<HTMLElement>(".rs-fy")!;
    const delay = i * CARD_FLIGHT.stagger;
    anims.push(fx.animate([{ transform: `translateX(${dx}px)` }, { transform: "translateX(0)" }], { duration: CARD_FLIGHT.ms, delay, easing: "cubic-bezier(.25,.1,.45,1)", fill: "both" }));
    anims.push(fy.animate([
      { transform: `translateY(${dy}px) scale(.2) rotate(-25deg)`, opacity: 0 },
      { transform: `translateY(${dy - 40}px) scale(.45) rotate(-10deg)`, opacity: 1, offset: 0.18 },
      { transform: "translateY(0) scale(1) rotate(0deg)", opacity: 1 },
    ], { duration: CARD_FLIGHT.ms, delay, easing: "cubic-bezier(.3,.9,.45,1.12)", fill: "both" }));
    later(delay + CARD_FLIGHT.ms - 40, () => audio.play("cardFlip", 0, 0.85));
  });

  // 2. then over, one at a time; the good ones held glowing first
  let t = (slots.length - 1) * CARD_FLIGHT.stagger + CARD_FLIGHT.ms + 260;
  for (const s of slots) {
    const hold = cardHoldMs(s.rank, s.best);
    if (hold > 0) later(t, () => s.slot.classList.add("charging"));
    t += hold;
    later(t, () => flipNow(s));
    t += CARD_FLIGHT.flipMs + CARD_BEAT_MS + (s.rank >= 3 ? 420 : 0);
  }
  later(t, finish);
  return { finish };
}
